import type { PaginationState } from '@tanstack/react-table';
import { FileText } from 'lucide-react';
import * as React from 'react';
import { EmptyState, ErrorState, Input, LoadingState, PageHeader } from '@payment-plan/ui';
import { SearchBar } from './components/SearchBar';
import { SeparationsTable } from './components/SeparationsTable';
import { useAdminSeparations, useFilterCatalogs } from './use-consultas';
import { useAuth } from '../../auth/auth-context';
import { can } from '../../lib/permissions';
import type { ConsultaAdminParams, SeparacionEstado } from '../../types/consultas';
import { NATIVE_SELECT_CLASS, TOUCH_INPUT_CLASS } from '../separations/components/field-styles';

const PAGE_SIZE = 20;
const ERROR_RANGO_ID = 'consulta-rango-error';
const AVISO_OTRA_INMOBILIARIA_ID = 'consulta-otra-inmobiliaria';

/**
 * Consulta administrativa de separaciones (HU-A05). El alcance lo decide el servidor por permiso
 * (ADR-014); el filtro de inmobiliaria solo se ofrece con `separacion.ver_todas_inmobiliarias`.
 */
export function AdminSeparationsPage(): React.JSX.Element {
  const { user } = useAuth();
  const permisos = user?.permissions;
  const verPlataforma = can(permisos, 'separacion.ver_todas_inmobiliarias');
  const puedeFiltrarProyecto = can(permisos, 'proyecto.ver');
  const puedeFiltrarAsesor = can(permisos, 'usuario.ver');

  const [q, setQ] = React.useState('');
  const [estado, setEstado] = React.useState<SeparacionEstado | ''>('');
  const [inmobiliariaId, setInmobiliariaId] = React.useState('');
  const [proyectoId, setProyectoId] = React.useState('');
  const [asesorId, setAsesorId] = React.useState('');
  const [desde, setDesde] = React.useState('');
  const [hasta, setHasta] = React.useState('');
  const [paginacion, setPaginacion] = React.useState<PaginationState>({ pageIndex: 0, pageSize: PAGE_SIZE });

  const catalogos = useFilterCatalogs({
    proyectos: puedeFiltrarProyecto,
    asesores: puedeFiltrarAsesor,
    inmobiliarias: verPlataforma,
  });

  // B-02: los catálogos de proyecto/asesor son del tenant del token; para otra inmobiliaria no aplican.
  const otraInmobiliaria = Boolean(inmobiliariaId) && inmobiliariaId !== user?.inmobiliariaId;
  const rangoInvalido = Boolean(desde && hasta && desde > hasta);

  const params = React.useMemo<ConsultaAdminParams>(
    () => ({
      ...(inmobiliariaId ? { inmobiliariaId } : {}),
      ...(proyectoId && !otraInmobiliaria ? { proyectoId } : {}),
      ...(asesorId && !otraInmobiliaria ? { asesorId } : {}),
      ...(desde ? { desde } : {}),
      ...(hasta ? { hasta } : {}),
      ...(q ? { q } : {}),
      ...(estado ? { estado } : {}),
      page: paginacion.pageIndex + 1,
      pageSize: paginacion.pageSize,
    }),
    [inmobiliariaId, proyectoId, asesorId, desde, hasta, q, estado, paginacion, otraInmobiliaria],
  );
  const { data, isLoading, isError, refetch, isFetching } = useAdminSeparations(params, !rangoInvalido);
  const conFiltros = Object.keys(params).length > 2;

  function conPaginaUno<T>(setter: (valor: T) => void) {
    return (valor: T) => {
      setter(valor);
      setPaginacion((p) => ({ ...p, pageIndex: 0 }));
    };
  }

  return (
    <div className="mx-auto w-full max-w-6xl space-y-6">
      <PageHeader
        eyebrow="Administración"
        title="Separaciones"
        description="Seguimiento de las separaciones registradas y de sus documentos emitidos."
      />

      <section aria-label="Filtros" className="space-y-3 rounded-xl border border-border/70 bg-card p-4 shadow-elegant">
        <SearchBar onBuscar={conPaginaUno(setQ)} estado={estado} onEstado={conPaginaUno(setEstado)} />

        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
          {verPlataforma && (
            <div className="space-y-1.5">
              <label htmlFor="consulta-inmobiliaria" className="text-sm font-medium text-foreground">
                Inmobiliaria
              </label>
              <select
                id="consulta-inmobiliaria"
                value={inmobiliariaId}
                onChange={(event) => {
                  const valor = event.target.value;
                  setInmobiliariaId(valor);
                  if (valor && valor !== user?.inmobiliariaId) {
                    setProyectoId('');
                    setAsesorId('');
                  }
                  setPaginacion((p) => ({ ...p, pageIndex: 0 }));
                }}
                className={NATIVE_SELECT_CLASS}
              >
                <option value="">Todas las inmobiliarias</option>
                {catalogos.inmobiliarias.map((i) => (
                  <option key={i.id} value={i.id}>
                    {i.nombre}
                  </option>
                ))}
              </select>
            </div>
          )}

          {puedeFiltrarProyecto && (
            <div className="space-y-1.5">
              <label htmlFor="consulta-proyecto" className="text-sm font-medium text-foreground">
                Proyecto
              </label>
              <select
                id="consulta-proyecto"
                value={otraInmobiliaria ? '' : proyectoId}
                disabled={otraInmobiliaria}
                aria-describedby={otraInmobiliaria ? AVISO_OTRA_INMOBILIARIA_ID : undefined}
                onChange={(event) => conPaginaUno(setProyectoId)(event.target.value)}
                className={NATIVE_SELECT_CLASS}
              >
                <option value="">Todos los proyectos</option>
                {catalogos.proyectos.map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.nombre}
                  </option>
                ))}
              </select>
            </div>
          )}

          {puedeFiltrarAsesor && (
            <div className="space-y-1.5">
              <label htmlFor="consulta-asesor" className="text-sm font-medium text-foreground">
                Asesor
              </label>
              <select
                id="consulta-asesor"
                value={otraInmobiliaria ? '' : asesorId}
                disabled={otraInmobiliaria}
                aria-describedby={otraInmobiliaria ? AVISO_OTRA_INMOBILIARIA_ID : undefined}
                onChange={(event) => conPaginaUno(setAsesorId)(event.target.value)}
                className={NATIVE_SELECT_CLASS}
              >
                <option value="">Todos los asesores</option>
                {catalogos.asesores.map((a) => (
                  <option key={a.id} value={a.id}>
                    {a.nombre}
                  </option>
                ))}
              </select>
            </div>
          )}

          <div className="space-y-1.5">
            <label htmlFor="consulta-desde" className="text-sm font-medium text-foreground">
              Desde
            </label>
            <Input
              id="consulta-desde"
              type="date"
              value={desde}
              max={hasta || undefined}
              onChange={(event) => conPaginaUno(setDesde)(event.target.value)}
              className={TOUCH_INPUT_CLASS}
            />
          </div>

          <div className="space-y-1.5">
            <label htmlFor="consulta-hasta" className="text-sm font-medium text-foreground">
              Hasta
            </label>
            <Input
              id="consulta-hasta"
              type="date"
              value={hasta}
              min={desde || undefined}
              aria-invalid={rangoInvalido}
              aria-describedby={rangoInvalido ? ERROR_RANGO_ID : undefined}
              onChange={(event) => conPaginaUno(setHasta)(event.target.value)}
              className={TOUCH_INPUT_CLASS}
            />
          </div>
        </div>

        {rangoInvalido && (
          <p id={ERROR_RANGO_ID} role="alert" className="text-sm font-medium text-destructive">
            La fecha «Desde» no puede ser posterior a «Hasta».
          </p>
        )}
        {otraInmobiliaria && (
          <p id={AVISO_OTRA_INMOBILIARIA_ID} className="text-sm text-muted-foreground">
            Los filtros de proyecto y asesor solo están disponibles para tu inmobiliaria.
          </p>
        )}
      </section>

      {isLoading && !rangoInvalido ? (
        <LoadingState message="Cargando separaciones" />
      ) : isError ? (
        <ErrorState
          message="No fue posible cargar las separaciones. Intenta de nuevo en un momento."
          onRetry={() => void refetch()}
        />
      ) : (data?.data.length ?? 0) === 0 ? (
        <EmptyState
          icon={<FileText className="h-8 w-8 text-muted-foreground" aria-hidden="true" />}
          title={conFiltros ? 'Sin resultados' : 'Aún no hay separaciones'}
          description={
            conFiltros
              ? 'Ninguna separación coincide con los filtros elegidos.'
              : 'Las separaciones que registren los asesores aparecerán aquí.'
          }
        />
      ) : (
        <SeparationsTable
          filas={data?.data ?? []}
          total={data?.meta.total ?? 0}
          paginacion={paginacion}
          onPaginacion={setPaginacion}
          rutaDetalle="/admin/separations"
          mostrarAsesor
          mostrarInmobiliaria={verPlataforma}
          cargando={isFetching && !data}
        />
      )}
    </div>
  );
}
